import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PALETTES } from '../../palette';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { Label } from '../pilot-ui/label';
import { RadioGroup, RadioGroupItem } from '../pilot-ui/radio-group';
import Button from '../ui/Button';

const ColourPalettePanel: React.FC = () => {
  const { savedPalette, draftPalette, paletteLoading, paletteError, previewPalette, cancelPalettePreview, savePalette } = useAuth();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const changed = draftPalette !== null;

  useEffect(() => () => cancelPalettePreview(), [cancelPalettePreview]);

  const choosePalette = (value: string) => {
    const palette = PALETTES.find((option) => option.id === value)?.id;
    if (!palette) return;
    setActionError(null);
    previewPalette(palette);
  };

  const cancel = () => {
    setActionError(null);
    cancelPalettePreview();
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      if (draftPalette) await savePalette(draftPalette);
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3" aria-label="Colour palette settings">
      {paletteLoading && <p className="text-muted-foreground">Loading...</p>}
      {paletteError && <p id="palette-load-error" role="alert" className="text-sm text-destructive">{paletteError}</p>}
      {actionError && <p id="palette-save-error" role="alert" className="rounded-sm border border-destructive p-2 text-sm text-destructive">{actionError}</p>}
      <p id="palette-help" className="text-sm text-muted-foreground">Choose the colours used by everyone in this console. Changes are previewed until saved.</p>
      <p className="text-sm text-muted-foreground">Saved palette: {PALETTES.find((palette) => palette.id === savedPalette)?.name}</p>
      <RadioGroup
        aria-label="Colour palette"
        aria-describedby={`palette-help${paletteError ? ' palette-load-error' : ''}${actionError ? ' palette-save-error' : ''}`}
        value={draftPalette ?? savedPalette}
        onValueChange={choosePalette}
        disabled={paletteLoading || busy}
      >
        {PALETTES.map((palette) => (
          <Label key={palette.id} className="flex items-start gap-3 rounded-md border border-border p-3">
            <RadioGroupItem value={palette.id} className="mt-0.5" />
            <span className="grid gap-1">
              <span>{palette.name}</span>
              <span className="text-sm font-normal text-muted-foreground">{palette.description}</span>
            </span>
          </Label>
        ))}
      </RadioGroup>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" type="submit" title={changed ? 'Save this palette for everyone in the console' : 'No changes to save'} disabled={!changed || busy || paletteLoading} aria-describedby={!changed ? 'palette-save-disabled-reason' : undefined}>
          {busy ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="secondary" title="Discard the preview and restore the saved palette" onClick={cancel} disabled={!changed || busy}>Cancel</Button>
        {!changed && <p id="palette-save-disabled-reason" className="text-xs text-muted-foreground">No changes to save.</p>}
      </div>
    </form>
  );
};

export default ColourPalettePanel;
